"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.retryPermissionUpdate = exports.fetchInstance = exports.shopDomain = exports.ApiError = void 0;
exports.setApiAppBridge = setApiAppBridge;
const app_bridge_1 = require("@shopify/app-bridge");
const app_bridge_utils_1 = require("@shopify/app-bridge-utils");
const actions_1 = require("@shopify/app-bridge/actions");
const apiUrl = process.env.REACT_APP_API_BASE_URL;
// A non-ok response's JSON body (e.g. RuleService.assertStockSatisfiesMinimum's
// `{ code: 'INSUFFICIENT_STOCK', products: [...] }`) used to be discarded —
// only ever reachable by re-parsing it back out of `error.message`'s
// `HTTP_400: <raw text>` string. `.message` is left exactly as it was (nothing
// depends on that format changing), `.body` is purely additive so a caller
// that wants the structured error can read it directly.
class ApiError extends Error {
    constructor(status, message, body) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}
exports.ApiError = ApiError;
exports.shopDomain = undefined;
let shopifyApp = undefined;
/**
 * IMPORTANT:
 * Every time you add/remove scopes, increase this version.
 * Example: v1 -> v2
 */
// v6: Pro feature B (stock-aware availability) added read_locations +
// read_inventory to the app's access_scopes. Without them the GraphQL
// `locations` query fails with "Access denied for locations field" (the
// location object has been gated behind read_locations since API 2024-07)
// and inventory levels can't be read at all — the feature silently resolves
// no stock. Bumping this version forces already-installed shops back through
// the scope-grant flow instead of leaving them permanently broken.
const SCOPES_VERSION = 'v6';
const REQUIRED_SCOPES = 'write_products,read_products,write_orders,read_price_rules,write_price_rules,read_discounts,write_discounts,read_themes,read_customers,write_customers,read_markets,read_cart_transforms,write_cart_transforms,read_validations,write_validations,read_locations,read_inventory';
const getReauthKey = (shop) => `reauth_status_${shop || 'unknown-shop'}_${SCOPES_VERSION}`;
const getReauthStatus = (shop) => {
    const key = getReauthKey(shop);
    return localStorage.getItem(key);
};
const setReauthStatus = (status, shop) => {
    const key = getReauthKey(shop);
    localStorage.setItem(key, status);
    // console.log('[fetchInstance] reauth status updated ->', {
    //   key,
    //   status,
    // });
};
const clearReauthStatus = (shop) => {
    const key = getReauthKey(shop);
    localStorage.removeItem(key);
    // console.log('[fetchInstance] reauth status cleared ->', {
    //   key,
    // });
};
const buildPermissionUrl = (shop) => {
    const redirectUri = `${apiUrl}/api/offline/auth`;
    const state = String(Date.now());
    const permissionUrl = `https://${shop}/admin/oauth/authorize?` +
        `client_id=${process.env.REACT_APP_API_KEY}&` +
        `scope=${encodeURIComponent(REQUIRED_SCOPES)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `state=${state}&` +
        `grant_options[]=`;
    // console.log('[fetchInstance] buildPermissionUrl ->', {
    //   shop,
    //   redirectUri,
    //   state,
    //   permissionUrl,
    //   scopesVersion: SCOPES_VERSION,
    //   scopes: REQUIRED_SCOPES,
    // });
    return permissionUrl;
};
const redirectToStoreAdmin = () => {
    if (!exports.shopDomain) {
        console.warn('[fetchInstance] redirectToStoreAdmin skipped -> shopDomain missing');
        return;
    }
    // Mirror what the backend afterAuth handler does on a successful OAuth
    // round-trip: hop the TOP window back to the embedded app URL. We
    // deliberately do NOT navigate to admin.shopify.com/store/<name> any more.
    // That URL ships X-Frame-Options: DENY, so:
    //   • assigning window.location.href to it leaves the iframe stuck on a
    //     "refused to connect" page (the original Mac spin bug);
    //   • assigning window.top.location.href to it boots the merchant out of
    //     the app entirely (a worse regression, observed in production).
    // The app URL (https://{shop}/admin/apps/{handle}) is iframe-friendly —
    // Shopify wraps it back into the embedded admin shell — so the merchant
    // simply gets a fresh load of our app and stays exactly where they were.
    const appHandle = process.env.REACT_APP_SHOPIFY_APP_SLUG || '';
    const appUrl = `https://${exports.shopDomain}/admin/apps/${appHandle}`;
    try {
        if (window.top && window.top !== window.self) {
            window.top.location.href = appUrl;
            return;
        }
    }
    catch (e) {
        // window.top access denied (cross-origin) — fall through to same-frame.
    }
    window.location.href = appUrl;
};
const isLikelyCorsOrRedirectFailure = (error) => {
    if (!(error instanceof Error))
        return false;
    const message = error.message || '';
    const result = error instanceof TypeError ||
        message.includes('Failed to fetch') ||
        message.includes('NetworkError') ||
        message.includes('Load failed');
    // console.log('[fetchInstance] isLikelyCorsOrRedirectFailure ->', {
    //   message,
    //   result,
    //   error,
    // });
    return result;
};
const triggerReauthorization = (manual = false) => {
    const reauthKey = getReauthKey(exports.shopDomain);
    const reauthStatus = localStorage.getItem(reauthKey);
    // console.log('[fetchInstance] triggerReauthorization called ->', {
    //   shopDomain,
    //   reauthKey,
    //   reauthStatus,
    //   hasShopifyApp: !!shopifyApp,
    //   apiUrl,
    //   scopesVersion: SCOPES_VERSION,
    //   manual,
    // });
    if (!manual && (reauthStatus === 'pending' || reauthStatus === 'verified')) {
        // console.log('[fetchInstance] reauth already handled, skipping redirect ->', {
        //   reauthKey,
        //   reauthStatus,
        //   manual,
        // });
        throw new Error('REAUTH_ALREADY_HANDLED');
    }
    if (!shopifyApp) {
        // console.error('[fetchInstance] shopifyApp is not initialized');
        throw new Error('SHOPIFY_APP_NOT_INITIALIZED');
    }
    if (!exports.shopDomain) {
        // console.error('[fetchInstance] shopDomain is missing');
        throw new Error('SHOP_DOMAIN_MISSING');
    }
    const permissionUrl = buildPermissionUrl(exports.shopDomain);
    setReauthStatus('pending', exports.shopDomain);
    // console.log('[fetchInstance] localStorage set pending ->', {
    //   reauthKey,
    //   value: localStorage.getItem(reauthKey),
    // });
    const redirect = actions_1.Redirect.create(shopifyApp);
    // console.log('[fetchInstance] dispatching REMOTE redirect ->', {
    //   permissionUrl,
    // });
    redirect.dispatch(actions_1.Redirect.Action.REMOTE, permissionUrl);
    throw new Error('REAUTH_TRIGGERED');
};
function setApiAppBridge(data) {
    return __awaiter(this, void 0, void 0, function* () {
        // console.log('[setApiAppBridge] called ->', data);
        if (!!shopifyApp) {
            // console.log('[setApiAppBridge] shopifyApp already initialized, skipping');
            return;
        }
        exports.shopDomain = data.shopDomain;
        // console.log('[setApiAppBridge] creating app bridge ->', {
        //   apiKey: process.env.REACT_APP_API_KEY,
        //   host: data.host || '',
        //   shopDomain,
        // });
        shopifyApp = (0, app_bridge_1.createApp)({
            apiKey: process.env.REACT_APP_API_KEY,
            host: data.host || '',
            forceRedirect: false,
        });
        // console.log('[setApiAppBridge] app bridge created successfully ->', {
        //   shopDomain,
        //   hasShopifyApp: !!shopifyApp,
        // });
    });
}
const fetchInstance = (_a) => __awaiter(void 0, [_a], void 0, function* ({ url, method, params, data, headers, responseType, }) {
    const reauthKey = getReauthKey(exports.shopDomain);
    try {
        // console.log('[fetchInstance] started ->', {
        //   url,
        //   method,
        //   params,
        //   data,
        //   headers,
        //   responseType,
        //   apiUrl,
        //   shopDomain,
        //   reauthKey,
        //   reauthStatus: localStorage.getItem(reauthKey),
        //   hasShopifyApp: !!shopifyApp,
        //   scopesVersion: SCOPES_VERSION,
        // });
        const baseUrl = `${apiUrl}${url}`;
        const finalUrl = params ? `${baseUrl}?${new URLSearchParams(params)}` : baseUrl;
        // console.log('[fetchInstance] urls ->', {
        //   baseUrl,
        //   finalUrl,
        // });
        let token;
        if (!!shopifyApp) {
            // console.log('[fetchInstance] requesting session token...');
            token = yield (0, app_bridge_utils_1.getSessionToken)(shopifyApp);
        }
        else {
            // console.warn('[fetchInstance] shopifyApp is undefined before getSessionToken');
        }
        // console.log('[fetchInstance] get token ->', token);
        if (token == undefined && !finalUrl.includes('isInstalled') && !finalUrl.includes('plans') && !finalUrl.includes('storage')) {
            // console.error('[fetchInstance] SESSION_TOKEN_MISSING');
            throw new Error('SESSION_TOKEN_MISSING');
        }
        const response = yield fetch(finalUrl, Object.assign(Object.assign({ 
            // Orval generates lowercase methods ('get'/'post'/'patch'/...). The
            // Fetch spec only byte-uppercases DELETE/GET/HEAD/OPTIONS/POST/PUT when
            // normalizing a method — PATCH is deliberately NOT in that list, so a
            // lowercase 'patch' reaches the wire as-is and the CORS preflight sends
            // `Access-Control-Request-Method: patch`. The server answers
            // `Access-Control-Allow-Methods: GET,HEAD,PUT,PATCH,POST,DELETE`
            // (uppercase) and the browser's comparison is case-SENSITIVE, so the
            // request is blocked — which is why every GET/POST in this app works
            // and PATCH was the first method to break. Uppercasing here fixes it
            // for every endpoint at once, instead of per-call.
            method: method.toUpperCase(), redirect: 'manual' }, (data ? { body: JSON.stringify(data) } : {})), { headers: Object.assign(Object.assign({ 'Content-Type': 'application/json' }, (headers || {})), { Authorization: !!token ? `Bearer ${token}` : '', ShopDomain: exports.shopDomain || '', 'ngrok-skip-browser-warning': 'true' }) }));
        // console.log('[fetchInstance] raw response ->', response);
        // console.log('[fetchInstance] response details ->', {
        //   status: response.status,
        //   ok: response.ok,
        //   redirected: response.redirected,
        //   type: response.type,
        //   url: response.url,
        //   bodyUsed: response.bodyUsed,
        // });
        /**
         * Case 1:
         * backend returned redirect to auth/install and browser exposed it as opaqueredirect / status 0
         */
        if (response.type === 'opaqueredirect' || response.status === 0) {
            // console.warn('[fetchInstance] detected opaqueredirect or status 0 -> reauth check', {
            //   type: response.type,
            //   status: response.status,
            //   finalUrl,
            // });
            const currentStatus = getReauthStatus(exports.shopDomain);
            // User went to permission update flow and came back without approval
            if (currentStatus === 'pending') {
                // console.warn('[fetchInstance] user likely cancelled permission update -> clear status and go to admin', {
                //   currentStatus,
                //   finalUrl,
                // });
                clearReauthStatus(exports.shopDomain);
                redirectToStoreAdmin();
                throw new Error('REAUTH_CANCELLED');
            }
            triggerReauthorization(false);
        }
        /**
         * Case 2:
         * backend returned real 403
         */
        if (response.status === 403) {
            // console.warn('[fetchInstance] detected 403 response -> reauth check', {
            //   type: response.type,
            //   status: response.status,
            //   finalUrl,
            // });
            const currentStatus = getReauthStatus(exports.shopDomain);
            if (currentStatus === 'pending') {
                // console.warn('[fetchInstance] permission still missing after pending reauth -> clear status and go to admin', {
                //   currentStatus,
                //   finalUrl,
                // });
                clearReauthStatus(exports.shopDomain);
                redirectToStoreAdmin();
                throw new Error('REAUTH_CANCELLED');
            }
            triggerReauthorization(false);
        }
        if (!response.ok) {
            let errorText = '';
            let errorBody;
            try {
                errorText = yield response.text();
                errorBody = errorText ? JSON.parse(errorText) : undefined;
            }
            catch (readError) {
                // console.warn('[fetchInstance] could not read/parse error response ->', readError);
            }
            // console.error('[fetchInstance] non-ok response ->', {
            //   status: response.status,
            //   errorText,
            //   finalUrl,
            // });
            throw new ApiError(response.status, `HTTP_${response.status}${errorText ? `: ${errorText}` : ''}`, errorBody);
        }
        /**
         * If request succeeded after a pending reauth, mark as verified
         */
        if (localStorage.getItem(reauthKey) === 'pending') {
            setReauthStatus('verified', exports.shopDomain);
            // console.log('[fetchInstance] reauth status changed pending -> verified', {
            //   reauthKey,
            //   value: localStorage.getItem(reauthKey),
            // });
        }
        if (responseType === 'text') {
            const result = (yield response.text());
            // console.log('[fetchInstance] parsed text response ->', result);
            return result;
        }
        if (responseType === 'blob') {
            const result = (yield response.blob());
            // console.log('[fetchInstance] parsed blob response ->', result);
            return result;
        }
        // Fall back to the response's OWN Content-Type rather than assuming JSON.
        //
        // `responseType` above is effectively dead for generated calls: Orval's
        // client never emits it (grep rest/src/gen — not one call site passes it),
        // and the generated functions take only `(params)`, so an
        // `Api.foo(params, { responseType: 'text' })` at the call site is silently
        // dropped on the floor. Everything therefore lands here.
        //
        // Blindly calling .json() on a text/csv body throws a SyntaxError that
        // reaches the caller as an unexplained rejection — which is exactly how
        // both blocked-value CSV exports broke: the button spun, JSON.parse threw
        // on `createdAt,ruleId,...`, and the download never happened. Trusting
        // Content-Type fixes every text endpoint at once instead of per-call,
        // the same way the PATCH-uppercase fix above is done once here.
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('json')) {
            return (yield response.json());
        }
        if (contentType) {
            // A declared non-JSON body: text/csv from the exports, text/plain, ...
            return (yield response.text());
        }
        // No Content-Type at all (empty 200/204 body, some proxies). Prefer JSON
        // so existing callers keep their object, but hand back the raw text
        // instead of throwing a parse error when it isn't JSON.
        const raw = yield response.text();
        try {
            return JSON.parse(raw);
        }
        catch (_b) {
            return raw;
        }
    }
    catch (errors) {
        // console.log('[fetchInstance] came to general error ->', errors);
        const reauthStatus = localStorage.getItem(reauthKey);
        // console.log('[fetchInstance] catch block diagnostics ->', {
        //   error: errors,
        //   reauthKey,
        //   reauthStatus,
        //   shopDomain,
        //   hasShopifyApp: !!shopifyApp,
        //   scopesVersion: SCOPES_VERSION,
        // });
        if ((errors === null || errors === void 0 ? void 0 : errors.message) === 'REAUTH_CANCELLED') {
            // console.warn('[fetchInstance] stopping flow because user cancelled permission update');
            throw errors;
        }
        if ((errors === null || errors === void 0 ? void 0 : errors.message) === 'REAUTH_ALREADY_HANDLED' && !url.includes('plans') && !url.includes('storage')) {
            // console.warn('[fetchInstance] stopping flow because reauth is already handled');
            throw errors;
        }
        /**
         * Case 3:
         * fetch failed before usable response returned
         * often happens in redirect/cors/network style failures
         */
        if (isLikelyCorsOrRedirectFailure(errors) &&
            reauthStatus !== 'pending' &&
            reauthStatus !== 'verified') {
            // console.warn('[fetchInstance] likely CORS / redirect failure -> triggering reauth once', {
            //   reauthKey,
            //   reauthStatus,
            // });
            triggerReauthorization(false);
        }
        // If request fails after pending reauth, treat it as cancelled/no approval
        if (isLikelyCorsOrRedirectFailure(errors) && reauthStatus === 'pending') {
            // console.warn('[fetchInstance] likely user cancelled permission update from catch block -> clear status and go to admin', {
            //   reauthKey,
            //   reauthStatus,
            // });
            clearReauthStatus(exports.shopDomain);
            redirectToStoreAdmin();
            throw new Error('REAUTH_CANCELLED');
        }
        throw errors;
    }
});
exports.fetchInstance = fetchInstance;
const retryPermissionUpdate = () => {
    // console.log('[fetchInstance] retryPermissionUpdate called manually');
    const reauthKey = getReauthKey(exports.shopDomain);
    if (exports.shopDomain) {
        localStorage.removeItem(reauthKey);
    }
    triggerReauthorization(true);
};
exports.retryPermissionUpdate = retryPermissionUpdate;
exports.default = exports.fetchInstance;
